import { useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useT, useLocalePath, useLang } from "@/i18n";
import { useSeo, absoluteUrl } from "@/lib/seo";
import { ArrowRight, CheckCircle2, Microscope, ShieldCheck, Stethoscope } from "lucide-react";

const DE = {
  inLanguage: "de-AT",
  metaTitle: "Bakteriophagen-Therapie Österreich | Praxis IB Wien",
  metaDescription:
    "Phagentherapie bei chronischen und antibiotikaresistenten Infektionen: Ablauf, Indikationen, Diagnostik und Begleitung in Kooperation mit dem Eliava Institut Tiflis.",
  keywords:
    "Bakteriophagen, Phagentherapie, Phagentherapie Österreich, Bakteriophagen Therapie, Phagen, Antibiotikaresistenz, multiresistente Keime, Biofilm Infektion, Eliava Institut, chronische Blasenentzündung Phagen, MRSA, Phagogramm, Wien",
  home: "Startseite",
  breadcrumbLabel: "Brotkrumen",
  eyebrow: "Kooperation mit dem Eliava Institut Tiflis",
  h1: "Bakteriophagen-Therapie: Phagen bei chronischen und antibiotikaresistenten Infektionen",
  intro:
    "Bakteriophagen sind natürlich vorkommende Viren, die ausschließlich Bakterien befallen – hochspezifisch, ohne das übrige Mikrobiom zu zerstören. Ich begleite Sie in Wien (Zoom nach Vereinbarung) als Vermittlerin zwischen Mikrobiologie, Diagnostik und Ihren behandelnden Ärztinnen und Ärzten, in Zusammenarbeit mit dem Eliava Institut in Tiflis.",
  quick: [
    "Hochspezifisch: trifft den Zielkeim, schont die Darm- und Schleimhautflora",
    "Wirksam auch dort, wo Antibiotikaresistenzen bestehen",
    "Kann Biofilme enzymatisch durchdringen",
    "Immer ergänzend und in Abstimmung mit Ihren Ärztinnen und Ärzten",
  ],
  sections: [
    {
      h: "Was sind Bakteriophagen?",
      p: [
        "Bakteriophagen – wörtlich „Bakterienfresser“ – sind Viren, die ausschließlich Bakterien infizieren. Sie besitzen keine Rezeptorstrukturen für menschliche Zellen und können diese daher weder befallen noch schädigen. Mit rund 10³¹ Partikeln gelten sie als die häufigsten biologischen Einheiten der Erde.",
        "Ein Phage erkennt über seine Schwanzfibern eine ganz bestimmte Oberflächenstruktur „seines“ Bakteriums, schleust sein Erbgut ein, vermehrt sich darin und löst die Zelle am Ende auf. Aus dieser Rezeptorbindung ergibt sich das entscheidende Merkmal: die Wirtsspezifität. Ein Phage trifft meist nur eine Bakterienart, häufig sogar nur einzelne Stämme.",
      ],
    },
    {
      h: "Wie funktioniert eine Phagentherapie?",
      p: [
        "Am Anfang steht immer ein valider Erregernachweis auf Stammebene. Aus Ihrem Isolat wird im Labor ein Phagogramm erstellt – eine Sensibilitätstestung, die prüft, welcher Phage Ihren Erreger tatsächlich auflöst. Erst dann wird ein standardisiertes Präparat aus der Phagenbank ausgewählt oder, wenn keines anspricht, ein personalisierter Phage gegen Ihren Stamm isoliert und vermehrt.",
        "Pharmakologisch verhalten sich Phagen anders als Antibiotika: Solange der Zielkeim vorhanden ist, vermehren sie sich direkt am Infektionsort („Auto-Dosing“). Ist die Bakterienpopulation eliminiert, endet die Vermehrung von selbst.",
      ],
    },
    {
      h: "Bei welchen Infektionen kommen Phagen infrage?",
      p: [
        "Phagen sind keine Option für den banalen Infekt. Relevant werden sie dort, wo Infektionen chronifizieren, immer wiederkehren oder unter leitliniengerechter Antibiose nicht ausheilen:",
      ],
      list: [
        "chronische Wundinfektionen, Ulcera cruris, diabetisches Fußsyndrom, nicht heilende Operationswunden",
        "Osteomyelitis, Knochen- und Weichteilinfekte, offenes Knie",
        "implantatassoziierte Infektionen an Prothesen, Osteosynthesematerial und Kathetern",
        "rezidivierende Blasenentzündung, komplizierte Harnwegsinfekte, chronische bakterielle Prostatitis",
        "chronische Rhinosinusitis, chronische Atemwegsinfekte",
        "nachgewiesene Besiedelung oder Infektion mit multiresistenten Keimen (MRSA, Pseudomonas, Klebsiella, ESBL-E.-coli, Enterokokken)",
      ],
    },
    {
      h: "Warum Phagen wirken, wo Antibiotika versagen",
      p: [
        "Antibiotikaresistenz beruht auf Betalaktamasen, Effluxpumpen, Zielstrukturmutationen oder verminderter Permeabilität – keiner dieser Mechanismen schützt vor einem Phagen, weil dieser einen völlig anderen Angriffsweg nutzt.",
        "Ein großer Teil chronischer Infekte ist zudem biofilmassoziiert: Die Bakterien liegen geschützt in einer selbst gebildeten Matrix und sind gegenüber Antibiotika bis zu tausendfach toleranter. Viele Phagen kodieren Depolymerasen, die genau diese Matrix enzymatisch aufbrechen.",
      ],
    },
    {
      h: "Ablauf Ihrer Begleitung in meiner Praxis",
      p: [
        "Wir sichten gemeinsam Ihre Vorbefunde und klären, ob eine Phagentherapie in Ihrem Fall plausibel und aussichtsreich ist. Ich organisiere die notwendige mikrobiologische Vordiagnostik – Erregernachweis, Resistenzlage und bei Bedarf DNA-basierte Verfahren wie qPCR oder NGS – und stelle den Kontakt zum Eliava Institut her.",
        "Danach halte ich die Kommunikation zwischen Ihnen, dem Institut und Ihren behandelnden Ärztinnen und Ärzten in Gang und begleite Sie therapeutisch vor, während und nach der Behandlung.",
      ],
    },
    {
      h: "Das Eliava Institut in Tiflis – und meine eigene Geschichte",
      p: [
        "Das George Eliava Institut für Bakteriophagen, Mikrobiologie und Virologie wurde 1923 gegründet und hat die Phagentherapie ohne Unterbrechung weitergeführt, während sie im Westen nach Einführung der Antibiotika weitgehend verlassen wurde. Dort ist eine der weltweit größten Phagenbibliotheken entstanden.",
        "Diesen Weg kenne ich nicht nur aus der Fachliteratur: Ich war vor einigen Jahren selbst als Patientin am Eliava Institut in Behandlung. Ich kenne die Abläufe, die Diagnostik und die Menschen vor Ort – und begleite Sie deshalb nicht aus theoretischer Distanz.",
      ],
    },
  ],
  faqHeading: "Häufige Fragen zur Phagentherapie",
  faq: [
    {
      q: "Was sind Bakteriophagen?",
      a: "Bakteriophagen sind natürlich vorkommende Viren, die ausschließlich Bakterien infizieren und auflösen. Menschliche Zellen tragen keine passenden Bindungsstellen und bleiben unberührt.",
    },
    {
      q: "Wann ist eine Phagentherapie sinnvoll?",
      a: "Vor allem bei chronischen, wiederkehrenden oder biofilmassoziierten Infektionen und bei nachgewiesenen multiresistenten Keimen, wenn Antibiotika nicht mehr ausreichend wirken. Voraussetzung ist ein Erregernachweis auf Stammebene.",
    },
    {
      q: "Wie läuft eine Phagentherapie ab?",
      a: "Erstgespräch (vorzugsweise persönlich) und Befundsichtung, mikrobiologische Diagnostik, Phagogramm (Sensibilitätstestung) am Institut, Auswahl eines standardisierten oder personalisierten Phagenpräparats, Behandlung und anschließende Verlaufskontrolle.",
    },
    {
      q: "Sind Phagen für den Menschen gefährlich?",
      a: "Phagen binden ausschließlich an bakterielle Oberflächenstrukturen und sind für menschliche Zellen nicht infektiös. Beschrieben werden überwiegend milde, vorübergehende Reaktionen zu Therapiebeginn.",
    },
    {
      q: "Ersetzt die Phagentherapie Antibiotika oder eine Operation?",
      a: "Nein. Sie wird ergänzend und immer in Abstimmung mit Ihren behandelnden Ärztinnen und Ärzten eingesetzt und ersetzt keine notwendige ärztliche Behandlung.",
    },
    {
      q: "Kann jeder Erreger mit Phagen behandelt werden?",
      a: "Nein. Ohne sauberen Erregernachweis und ohne positives Phagogramm ist keine Therapie möglich – nicht jeder Stamm ist phagensensibel.",
    },
  ],
  articleHeading: "Ausführlich zum Nachlesen",
  articleText:
    "Im Magazin finden Sie den wissenschaftlichen Hintergrundartikel: lytischer Zyklus, Depolymerasen, Biofilm, Auto-Dosing und Phage Steering – akademisch fundiert und verständlich erklärt.",
  articleCta: "Zum Magazinartikel über Bakteriophagen",
  ctaHeading: "Sie tragen eine chronische Infektion mit sich?",
  ctaText:
    "Wir sehen uns Ihre Befunde in Ruhe gemeinsam an und klären, ob dieser Weg für Sie infrage kommt – in der Praxis in Wien (Zoom nach Vereinbarung).",
  contact: "Kontakt aufnehmen",
  services: "Alle Leistungen ansehen",
  note: "Hinweis: Die Phagentherapie ist in Österreich und der EU kein zugelassenes Arzneimittel. Sie erfolgt als individueller Heilversuch, ergänzend zur ärztlichen Behandlung.",
};

const EN = {
  inLanguage: "en",
  metaTitle: "Bacteriophage therapy in Austria | Praxis IB Vienna",
  metaDescription:
    "Phage therapy for chronic and antibiotic-resistant infections: indications, diagnostics, process and guidance in cooperation with the Eliava Institute in Tbilisi.",
  keywords:
    "bacteriophages, phage therapy, phage therapy Austria, antibiotic resistance, multi-resistant bacteria, biofilm infection, Eliava Institute, MRSA, recurrent cystitis, Vienna",
  home: "Home",
  breadcrumbLabel: "Breadcrumb",
  eyebrow: "In cooperation with the Eliava Institute, Tbilisi",
  h1: "Bacteriophage therapy: phages for chronic and antibiotic-resistant infections",
  intro:
    "Bacteriophages are naturally occurring viruses that infect bacteria only – highly specific, without destroying the rest of the microbiome. I accompany you in Vienna (Zoom by arrangement) as an intermediary between microbiology, diagnostics and your treating physicians, working with the Eliava Institute in Tbilisi.",
  quick: [
    "Highly specific: hits the target organism, spares gut and mucosal flora",
    "Effective even where antibiotic resistance exists",
    "Able to penetrate biofilms enzymatically",
    "Always complementary and coordinated with your physicians",
  ],
  sections: [
    {
      h: "What are bacteriophages?",
      p: [
        "Bacteriophages – literally “bacteria eaters” – are viruses that infect bacteria exclusively. They carry no receptor structures for human cells and can therefore neither enter nor damage them. With around 10³¹ particles they are the most abundant biological entities on earth.",
        "A phage recognises a very specific surface structure of “its” bacterium via its tail fibres, injects its genome, replicates inside the cell and finally dissolves it. This receptor binding produces the decisive feature: host specificity. A phage usually attacks only one bacterial species, often only particular strains.",
      ],
    },
    {
      h: "How does phage therapy work?",
      p: [
        "Everything starts with a valid pathogen identification at strain level. A phagogram – a sensitivity test – determines which phage actually lyses your organism. Only then is a standardised preparation selected from the phage bank or, if none responds, a personalised phage isolated and propagated against your strain.",
        "Pharmacologically phages behave differently from antibiotics: as long as the target organism is present they multiply at the site of infection (“auto-dosing”). Once the bacterial population is eliminated, replication stops by itself.",
      ],
    },
    {
      h: "Which infections are suitable?",
      p: [
        "Phages are not an option for a trivial infection. They become relevant where infections become chronic, keep recurring or fail to heal under guideline-based antibiotic treatment:",
      ],
      list: [
        "chronic wound infections, leg ulcers, diabetic foot syndrome, non-healing surgical wounds",
        "osteomyelitis, bone and soft-tissue infections, open knee",
        "implant-associated infections of prostheses, osteosynthesis material and catheters",
        "recurrent cystitis, complicated urinary tract infections, chronic bacterial prostatitis",
        "chronic rhinosinusitis and chronic respiratory infections",
        "documented colonisation or infection with multi-resistant organisms (MRSA, Pseudomonas, Klebsiella, ESBL E. coli, enterococci)",
      ],
    },
    {
      h: "Why phages work where antibiotics fail",
      p: [
        "Antibiotic resistance relies on beta-lactamases, efflux pumps, target mutations or reduced permeability – none of these mechanisms protects against a phage, because it uses an entirely different route of attack.",
        "Many chronic infections are also biofilm-associated: bacteria sit protected inside a self-produced matrix and tolerate antibiotics up to a thousand times better. Many phages encode depolymerases that break down exactly this matrix.",
      ],
    },
    {
      h: "How I accompany you",
      p: [
        "Together we review your existing findings and clarify whether phage therapy is plausible and promising in your case. I organise the necessary microbiological work-up – pathogen detection, resistance profile and, where needed, DNA-based methods such as qPCR or NGS – and establish contact with the Eliava Institute.",
        "From there I keep communication flowing between you, the institute and your treating physicians, and support you therapeutically before, during and after treatment.",
      ],
    },
    {
      h: "The Eliava Institute in Tbilisi – and my own story",
      p: [
        "The George Eliava Institute of Bacteriophages, Microbiology and Virology was founded in 1923 and has continued phage therapy without interruption, while the West largely abandoned it after antibiotics arrived. One of the world's largest phage libraries has grown there.",
        "I know this path not only from the literature: some years ago I was treated at the Eliava Institute as a patient myself. I know the processes, the diagnostics and the people on site – which is why I do not accompany you from a theoretical distance.",
      ],
    },
  ],
  faqHeading: "Frequently asked questions about phage therapy",
  faq: [
    {
      q: "What are bacteriophages?",
      a: "Bacteriophages are naturally occurring viruses that infect and dissolve bacteria only. Human cells carry no matching binding sites and remain untouched.",
    },
    {
      q: "When is phage therapy useful?",
      a: "Mainly for chronic, recurrent or biofilm-associated infections and for documented multi-resistant organisms when antibiotics no longer work well enough. A pathogen identification at strain level is required.",
    },
    {
      q: "How does the process work?",
      a: "Initial consultation (preferably in person) and review of findings, microbiological diagnostics, phagogram at the institute, selection of a standardised or personalised phage preparation, treatment and follow-up.",
    },
    {
      q: "Are phages dangerous for humans?",
      a: "Phages bind exclusively to bacterial surface structures and are not infectious to human cells. Reported reactions are mostly mild and transient at the start of therapy.",
    },
    {
      q: "Does phage therapy replace antibiotics or surgery?",
      a: "No. It is used complementarily and always in coordination with your treating physicians; it does not replace necessary medical treatment.",
    },
    {
      q: "Can every pathogen be treated with phages?",
      a: "No. Without a clean pathogen identification and a positive phagogram there is no therapy – not every strain is phage-sensitive.",
    },
  ],
  articleHeading: "Read the science in depth",
  articleText:
    "The magazine article covers the scientific background: lytic cycle, depolymerases, biofilm, auto-dosing and phage steering – academically grounded and clearly explained.",
  articleCta: "Read the magazine article on bacteriophages",
  ctaHeading: "Living with a chronic infection?",
  ctaText:
    "We take a calm look at your findings together and clarify whether this path is an option for you – at the practice in Vienna (Zoom by arrangement).",
  contact: "Get in touch",
  services: "View all services",
  note: "Please note: phage therapy is not an approved medicinal product in Austria or the EU. It is applied as an individual treatment attempt, complementary to medical care.",
};

const ARTICLE_SLUG = "/blog/bakteriophagen-therapie-phagen-antibiotikaresistenz";

const Bakteriophagen = () => {
  const lang = useLang();
  const lp = useLocalePath();
  const t = useT(DE, EN);

  const jsonLd = useMemo(() => {
    const url = absoluteUrl(lang === "en" ? "/en/bakteriophagen-therapie" : "/bakteriophagen-therapie");
    return [
      {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: t.h1,
        description: t.metaDescription,
        url,
        inLanguage: t.inLanguage,
        about: {
          "@type": "MedicalTherapy",
          name: lang === "en" ? "Bacteriophage therapy" : "Bakteriophagen-Therapie",
          alternateName: lang === "en" ? ["Phage therapy"] : ["Phagentherapie", "Phagen-Therapie"],
},
        author: { "@id": absoluteUrl("/#therapeutin") },
        publisher: { "@id": absoluteUrl("/#business") },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: t.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t.home, item: absoluteUrl("/") },
          { "@type": "ListItem", position: 2, name: t.h1, item: url },
        ],
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useSeo({
    title: t.metaTitle,
    description: t.metaDescription,
    keywords: t.keywords,
    canonical: "/bakteriophagen-therapie",
    jsonLd,
    jsonLdId: "phagen-jsonld",
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="py-12 md:py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-3xl">
            <nav aria-label={t.breadcrumbLabel} className="text-sm text-muted-foreground mb-6">
              <Link to={lp("/")} className="hover:text-primary">
                {t.home}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-foreground">
                {lang === "en" ? "Bacteriophage therapy" : "Bakteriophagen-Therapie"}
              </span>
            </nav>
            <span className="text-primary font-medium text-sm uppercase tracking-wider">{t.eyebrow}</span>
            <h1 className="text-3xl md:text-4xl font-light text-foreground mt-2 mb-4 break-words">{t.h1}</h1>
            <p className="text-muted-foreground leading-relaxed">{t.intro}</p>

            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              {t.quick.map((q) => (
                <li key={q} className="flex gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <article className="py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-3xl space-y-10">
            {t.sections.map((s) => (
              <section key={s.h}>
                <h2 className="text-xl md:text-2xl font-medium text-foreground mb-3 break-words">{s.h}</h2>
                {s.p.map((p) => (
                  <p key={p} className="text-muted-foreground leading-relaxed mb-3">
                    {p}
                  </p>
                ))}
                {"list" in s && s.list && (
                  <ul className="space-y-2 mt-2">
                    {s.list.map((item) => (
                      <li key={item} className="flex gap-2 text-muted-foreground">
                        <Microscope className="w-4 h-4 text-primary shrink-0 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <section>
              <h2 className="text-xl md:text-2xl font-medium text-foreground mb-4">{t.faqHeading}</h2>
              <div className="space-y-4">
                {t.faq.map((f) => (
                  <Card key={f.q} className="border-border">
                    <CardContent className="p-5">
                      <h3 className="font-medium text-foreground mb-2">{f.q}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section className="bg-secondary/40 rounded-2xl p-6">
              <h2 className="text-xl font-medium text-foreground mb-2">{t.articleHeading}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">{t.articleText}</p>
              <Link
                to={lp(ARTICLE_SLUG)}
                className="inline-flex items-center gap-2 text-primary font-medium hover:text-primary/80"
              >
                {t.articleCta} <ArrowRight className="w-4 h-4" />
              </Link>
            </section>

            <section className="border border-border rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">{t.note}</p>
              </div>
            </section>

            <section className="text-center">
              <h2 className="text-2xl font-light text-foreground mb-3">{t.ctaHeading}</h2>
              <p className="text-muted-foreground mb-6">{t.ctaText}</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button asChild>
                  <Link to={lp("/#kontakt")}>
                    <Stethoscope className="w-4 h-4 mr-2" />
                    {t.contact}
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to={lp("/#leistungen")}>{t.services}</Link>
                </Button>
              </div>
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default Bakteriophagen;
